import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiHeart,
  FiFilm,
  FiPlay,
} from 'react-icons/fi';
import xtreamAPI, { XtreamContent } from '../../api/xtreamAPI';
import { useWatchProgress, formatProgressTime } from '../../contexts/WatchProgressContext';
import { useWatchHistory } from '../../contexts/WatchHistoryContext';
import {
  fetchMovieDetails,
  getRuntimeTmdbLanguage,
  TmdbMovieDetailsResult,
  TmdbMovieResult,
} from '../../api/tmdbAPI';
import { getCatalogRecommendations } from '../../api/backendAPI';
import { useDevice } from '../../contexts/DeviceContext';
import { getCachedVodStreams } from '../../utils/vodStreamsCache';
import { consumeCatalogForceRefresh } from '../../utils/catalogRefresh';
import { getCachedVodInfo, checkVodAccess } from '../../utils/vodInfoCache';
import { getMovieAutoplayMode, MovieAutoplayMode } from '../../utils/movieAutoplay';
import { getContentDataSource, ContentDataSource } from '../../utils/contentDataSource';
import {
  PlaybackOverlayStatus,
  PlaybackStatusOverlay,
} from '../../components/PlaybackStatusOverlay/PlaybackStatusOverlay';
import { VideoControlsOverlay, AudioTrackInfo, SubtitleTrackInfo } from '../../components/VideoControlsOverlay/VideoControlsOverlay';
import { SamsungAvPlayVod, shouldUseSamsungAvPlayVod } from '../../utils/samsungAvPlayVod';
import { TrailerModal } from '../../components/TrailerModal/TrailerModal';
import { AlternativesModal } from '../../components/AlternativesModal/AlternativesModal';
import { resolveContentIdentity } from '../../utils/contentIdentityResolver';

import './MovieDetails.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Chrome 47 compat: play() returns undefined instead of a Promise */
function safePlay(video: HTMLVideoElement): void {
  const p = video.play();
  if (p !== undefined) p.catch(() => {});
}

export interface MovieDetailsEntry {
  /** TMDB movie id */
  tmdbId: number;
  /** Pre-loaded title (shown while details load) */
  title?: string;
  /** Pre-loaded poster URL (used as favorite icon) */
  posterUrl?: string;
  /** Pre-loaded backdrop URL from trending/recommendations response */
  backdropUrl?: string;
  /** Xtream VOD stream_id — if present the mini-player can start immediately */
  vodStreamId?: string;
  /** Xtream category_id used by parental filtering and favorites. */
  categoryId?: string;
  /** When true, this movie was opened from a kids context (Kids page or kids-movies source). */
  isKids?: boolean;
  /** Metadata hints used only to validate a TMDB fallback search. */
  plot?: string;
  year?: string;
  releaseDate?: string;
  release_date?: string;
}

interface MovieDetailsProps {
  entry: MovieDetailsEntry;
  isFavorite?: boolean;
  isActive?: boolean;
  onFullScreen?: (vodStreamId: string) => void;
  onToggleFavorite?: (identity: string) => void;
  onBack?: () => void;
  onSelectRecommended?: (entry: MovieDetailsEntry) => void;
  onIdentityResolved?: (tmdbId: number) => void;
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface RecItem {
  tmdb: TmdbMovieResult;
  vodStreamId?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  } catch {
    return iso;
  }
}

// ─── Recommended card ────────────────────────────────────────────────────────

interface RecCardProps {
  movie?: TmdbMovieResult;
  loading?: boolean;
  onSelect: () => void;
  focused: boolean;
}

const RecCard: React.FC<RecCardProps> = ({ movie, loading, onSelect, focused }) => {
  const style: React.CSSProperties = movie?.posterUrl
    ? { backgroundImage: `url(${movie.posterUrl})` }
    : {};

  return (
    <div
      className={[
        'movie-details__rec-card',
        loading ? 'movie-details__rec-card--loading' : '',
        focused ? 'movie-details__rec-card--focused' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
      onClick={onSelect}
      tabIndex={-1}
    >
      {movie?.title && !loading && (
        <div className="movie-details__rec-card-title">{movie.title}</div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const MovieDetails: React.FC<MovieDetailsProps> = ({
  entry,
  isFavorite = false,
  isActive = true,
  onFullScreen,
  onToggleFavorite,
  onBack,
  onSelectRecommended,
  onIdentityResolved,
}) => {
  const { deviceId, activationPassword } = useDevice();
  const { t } = useTranslation();
  const [details, setDetails] = useState<TmdbMovieDetailsResult | null>(null);
  const [similar, setSimilar] = useState<RecItem[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(true);
  /** TMDB id resolved at runtime — may come from entry.tmdbId OR from Xtream info.tmdb_id */
  const [resolvedTmdbId, setResolvedTmdbId] = useState(entry.tmdbId);
  /** Raw Xtream stream name (e.g. "Cleaner [LEG]") — used for subtitle */
  const [xtreamVodName, setXtreamVodName] = useState<string | null>(null);
  const [focusSection, setFocusSection] = useState<'buttons' | 'recs'>('buttons');
  const [focusedBtnIdx, setFocusedBtnIdx] = useState(0);
  const [focusedRec, setFocusedRec] = useState(-1);

  const backdropsRef = useRef<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fsWrapRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<any>(null);
  const avPlayRef = useRef(new SamsungAvPlayVod());
  const forcedSubtitleAppliedRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAvPlayActive, setIsAvPlayActive] = useState(false);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [activeVodStreamId, setActiveVodStreamId] = useState<string | undefined>(entry.vodStreamId);
  const [primaryExt, setPrimaryExt] = useState('mp4');
  // Priority: 1=entry.backdropUrl (trending/recs) → 2=Xtream → 3=TMDB
  const [xtreamBackdropUrl, setXtreamBackdropUrl] = useState<string | null>(entry.backdropUrl ?? entry.posterUrl ?? null);
  const [videoStatus, setVideoStatus] = useState<PlaybackOverlayStatus | null>('connecting');
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [pendingSeekPos, setPendingSeekPos] = useState(0);
  const [resumeModalFocusedBtn, setResumeModalFocusedBtn] = useState(1); // 0 = from start, 1 = continue
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [showAlternativesModal, setShowAlternativesModal] = useState(false);
  const [movieAutoplayMode] = useState<MovieAutoplayMode>(getMovieAutoplayMode);
  const [contentDataSource] = useState<ContentDataSource>(getContentDataSource);

  const lastProgressSaveRef = useRef(0);
  const isFullscreenRef = useRef(false);
  const { getProgress, setProgress, clearProgress, flushProgress } = useWatchProgress();
  const { addToHistory } = useWatchHistory();

  // ── Audio / subtitle track state ─────────────────────────────────────────
  const [audioTracks, setAudioTracks] = useState<AudioTrackInfo[]>([]);
  const [activeAudioId, setActiveAudioId] = useState(0);
  const [subtitleTracks, setSubtitleTracks] = useState<SubtitleTrackInfo[]>([]);
  const [activeSubtitleId, setActiveSubtitleId] = useState(-1);

  // ── Reset state when entry changes ──────────────────────────────────────
  useEffect(() => {
    setDetails(null);
    setLoadingDetails(true);
    setStreamUrl(null);
    setResolvedTmdbId(entry.tmdbId);
    setXtreamVodName(null);
    // Keep pre-loaded backdrop visible while fetching (priority 1)
    setXtreamBackdropUrl(entry.backdropUrl ?? entry.posterUrl ?? null);
    setVideoStatus('connecting');
    setFocusSection('buttons');
    setFocusedBtnIdx(0);
    setFocusedRec(-1);
    setActiveVodStreamId(entry.vodStreamId);
    setSimilar([]);
    setLoadingSimilar(true);
    lastProgressSaveRef.current = 0;
  }, [entry.vodStreamId]);

  // ── Fetch details: stream URL from Xtream, metadata always from TMDB ────
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // ── Run Xtream fetch + TMDB details fetch in parallel ─────────────────
      // TMDB details (if tmdbId already known from prop) starts immediately —
      // no reason to wait for Xtream before hitting TMDB.

      const xtreamPromise = (async () => {
        if (!entry.vodStreamId) return null;
        const userStreams = await getCachedVodStreams();
        if (cancelled) return null;
        if (!checkVodAccess(entry.vodStreamId, userStreams)) {
          console.warn('⚠️ vodStreamId not in user stream list — skipping Xtream');
          return null;
        }
        try {
          const data: any = await getCachedVodInfo(entry.vodStreamId);
          return data ?? null;
        } catch {
          return null;
        }
      })();

      const tmdbPromise = contentDataSource === 'xtream' ? Promise.resolve(null) : (async () => {
        if (!entry.tmdbId) return null;
        try { return await fetchMovieDetails(entry.tmdbId); } catch { return null; }
      })();

      const [xtreamData, tmdbDetInitial] = await Promise.all([xtreamPromise, tmdbPromise]);
      if (cancelled) return;

      // ── Apply Xtream results ──────────────────────────────────────────────
      let xtreamTmdbId: number | undefined;
      if (xtreamData) {
        const ext: string = xtreamData.movie_data?.container_extension || 'mp4';
        setPrimaryExt(ext);
        const directSrc: string = xtreamData.movie_data?.direct_source || '';
        setStreamUrl(directSrc
          ? xtreamAPI.preparePlaybackUrl(directSrc)
          : xtreamAPI.buildStreamUrl(entry.vodStreamId!, 'movie', ext));
        const rawTmdbId = xtreamData.info?.tmdb_id;
        if (rawTmdbId) xtreamTmdbId = Number(rawTmdbId);
        const vodName: string = xtreamData.movie_data?.name || xtreamData.info?.name || '';
        if (vodName) setXtreamVodName(vodName);
      }

      // ── Xtream mode: build details from Xtream data only ─────────────────
      if (contentDataSource === 'xtream') {
        if (xtreamData) {
          const backdropRaw = xtreamData.info?.backdrop_path;
          const backdropUrl: string | null = Array.isArray(backdropRaw)
            ? (backdropRaw[0] || null)
            : (backdropRaw || null);
          const xtreamDet: TmdbMovieDetailsResult = {
            id: 0,
            title: xtreamData.info?.name || xtreamData.movie_data?.name || entry.title || '',
            originalTitle: xtreamData.info?.name || entry.title || '',
            overview: xtreamData.info?.plot || '',
            posterUrl: xtreamData.info?.cover_big || null,
            backdropUrl: backdropUrl || entry.backdropUrl || null,
            releaseDate: xtreamData.info?.releasedate || '',
            voteAverage: parseFloat(xtreamData.info?.rating || '0') || 0,
            genres: xtreamData.info?.genre ? [xtreamData.info.genre] : [],
            countries: [],
            director: xtreamData.info?.director || '',
            cast: xtreamData.info?.cast
              ? String(xtreamData.info.cast).split(',').map((s: string) => s.trim()).filter(Boolean)
              : [],
            trailerKey: xtreamData.info?.youtube_trailer || undefined,
          };
          setDetails(xtreamDet);
          if (xtreamDet.backdropUrl) setXtreamBackdropUrl(xtreamDet.backdropUrl);
          backdropsRef.current = xtreamDet.backdropUrl;
        }
        setLoadingDetails(false);
        return;
      }

      // ── TMDB mode: one trusted identity flow for every navigation source ──
      let det: TmdbMovieDetailsResult | null = tmdbDetInitial;
      try {
        const identity = await resolveContentIdentity({
          type: 'movie',
          streamId: entry.vodStreamId,
          entryTmdbId: entry.tmdbId,
          xtreamTmdbId,
          title: xtreamData?.movie_data?.name || xtreamData?.info?.name || entry.title || '',
          year: xtreamData?.info?.releasedate || entry.year || entry.releaseDate || entry.release_date,
          plot: xtreamData?.info?.plot || entry.plot,
        });
        if (cancelled) return;
        if (identity.tmdbId) {
          setResolvedTmdbId(identity.tmdbId);
          if (identity.tmdbId !== entry.tmdbId) onIdentityResolved?.(identity.tmdbId);
        }
        det = identity.details as TmdbMovieDetailsResult | null;
      } catch {
        // Xtream fallback below keeps the screen usable.
      }

      if (cancelled) return;

      if (det) {
        setDetails(det);
        if (det.backdropUrl) setXtreamBackdropUrl(det.backdropUrl);
        backdropsRef.current = det.backdropUrl;
      } else if (xtreamData) {
        // TMDB found nothing — fall back to whatever Xtream vod/info provides
        const backdropRaw = xtreamData.info?.backdrop_path;
        const backdropUrl: string | null = Array.isArray(backdropRaw)
          ? (backdropRaw[0] || null)
          : (backdropRaw || null);
        const fallback: TmdbMovieDetailsResult = {
          id: 0,
          title: xtreamData.info?.name || xtreamData.movie_data?.name || entry.title || '',
          originalTitle: xtreamData.info?.name || entry.title || '',
          overview: xtreamData.info?.plot || '',
          posterUrl: xtreamData.info?.cover_big || null,
          backdropUrl: backdropUrl || entry.backdropUrl || null,
          releaseDate: xtreamData.info?.releasedate || '',
          voteAverage: parseFloat(xtreamData.info?.rating || '0') || 0,
          genres: xtreamData.info?.genre ? [xtreamData.info.genre] : [],
          countries: [],
          director: xtreamData.info?.director || '',
          cast: xtreamData.info?.cast
            ? String(xtreamData.info.cast).split(',').map((s: string) => s.trim()).filter(Boolean)
            : [],
          trailerKey: xtreamData.info?.youtube_trailer || undefined,
        };
        setDetails(fallback);
        if (fallback.backdropUrl) setXtreamBackdropUrl(fallback.backdropUrl);
        backdropsRef.current = fallback.backdropUrl;
      }

      setLoadingDetails(false);
    })();

    return () => {
      cancelled = true;
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [entry.vodStreamId, entry.tmdbId, entry.title, entry.plot, entry.year, entry.releaseDate, entry.release_date, contentDataSource, onIdentityResolved]);

  // ── Fetch similar / recommended — starts only after details are loaded ───
  useEffect(() => {
    if (contentDataSource === 'xtream' || entry.isKids) {
      setLoadingSimilar(false);
      return;
    }
    if (loadingDetails) return; // wait for identity + details to finish first
    if (!resolvedTmdbId) {
      setLoadingSimilar(false);
      return;
    }
    let cancelled = false;
    setSimilar([]);
    setLoadingSimilar(true);

    (async () => {
      try {
        const results = await getCatalogRecommendations(deviceId, activationPassword, {
          type: 'movie',
          tmdbId: resolvedTmdbId,
          title: details?.title || entry.title,
          year: (details?.releaseDate || entry.releaseDate || entry.release_date || entry.year || '').slice(0, 4),
          plot: details?.overview || entry.plot,
          language: getRuntimeTmdbLanguage(),
        }, consumeCatalogForceRefresh('recommendations-movie'));
        if (cancelled) return;
        setSimilar(results.map((item) => ({
          tmdb: {
            id: item.tmdbId,
            title: item.title,
            overview: '',
            posterUrl: item.posterUrl,
            backdropUrl: item.backdropUrl,
            releaseDate: '',
            voteAverage: 0,
          },
          vodStreamId: item.streamId,
        })));
      } catch { /* silently degrade */ } finally {
        if (!cancelled) setLoadingSimilar(false);
      }
    })();

    return () => { cancelled = true; };
  }, [activationPassword, contentDataSource, details, deviceId, entry.isKids, entry.plot, entry.releaseDate, entry.release_date, entry.title, entry.year, loadingDetails, resolvedTmdbId]);



  // ── Init/destroy HLS or native video when streamUrl changes ──────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !streamUrl) return;
    hlsRef.current?.destroy();
    hlsRef.current = null;
    forcedSubtitleAppliedRef.current = false;
    setAudioTracks([]);
    setActiveAudioId(0);
    setSubtitleTracks([]);
    setActiveSubtitleId(-1);

    setVideoStatus('connecting');

    const isHls = streamUrl.includes('.m3u8');
    const useAvPlay = shouldUseSamsungAvPlayVod(primaryExt, streamUrl);
    if (isHls) {
      Promise.resolve().then(() => {
        const Hls: typeof import('hls.js').default = (window as any).Hls;
        if (!videoRef.current) return;
        if (Hls && Hls.isSupported()) {
          const hls = new Hls({ autoStartLoad: true });
          hls.loadSource(streamUrl);
          hls.attachMedia(videoRef.current);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            // do not autoplay — user must press Assistir
          });
          hls.on(Hls.Events.ERROR, (_evt: any, data: any) => {
            if (data.fatal) setVideoStatus('error');
          });
          // Track detection
          hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, (_e: any, d: any) => {
            const tracks: AudioTrackInfo[] = (d?.audioTracks ?? []).map((track: any) => ({ id: track.id, name: track.name || track.lang || t('playback.audioTrack', { num: track.id + 1 }), lang: track.lang }));
            setAudioTracks(tracks);
            setActiveAudioId(hls.audioTrack >= 0 ? hls.audioTrack : 0);
          });
          hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, (_e: any, d: any) => {
            setActiveAudioId(typeof d?.id === 'number' ? d.id : hls.audioTrack);
          });
          hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, (_e: any, d: any) => {
            const tracks: SubtitleTrackInfo[] = (d?.subtitleTracks ?? []).map((track: any) => ({ id: track.id, name: track.name || track.lang || t('playback.subtitleTrack', { num: track.id + 1 }), lang: track.lang, forced: !!track.forced }));
            setSubtitleTracks(tracks);
            const forced = forcedSubtitleAppliedRef.current ? undefined : tracks.find(track => track.forced);
            if (forced) forcedSubtitleAppliedRef.current = true;
            if (forced) hls.subtitleTrack = forced.id;
            setActiveSubtitleId(forced?.id ?? hls.subtitleTrack);
          });
          hls.on(Hls.Events.SUBTITLE_TRACK_SWITCH, (_e: any, d: any) => {
            setActiveSubtitleId(typeof d?.id === 'number' ? d.id : hls.subtitleTrack);
          });
          hlsRef.current = hls;
        } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
          videoRef.current.src = streamUrl;
          // do not autoplay — user must press Assistir
        }
      });
    } else if (!useAvPlay) {
      video.src = streamUrl;
      // do not autoplay — user must press Assistir
    }

    // Bind video events for status overlay + progress tracking
    const onPlaying = () => setVideoStatus(null);
    const onWaiting  = () => setVideoStatus('buffering');
    const onCanPlay  = () => setVideoStatus(null);
    const onError    = () => setVideoStatus('error');
    const onTimeUpdate = () => {
      if (!activeVodStreamId) return;
      // Only track watch progress when actually watching in fullscreen
      if (!isFullscreenRef.current) return;
      const now = Date.now();
      // On the very first tick, record in watch history
      if (lastProgressSaveRef.current === 0) {
        addToHistory({
          id: String(resolvedTmdbId || entry.tmdbId || activeVodStreamId),
          type: 'movie',
          name: entry.title ?? details?.title ?? '',
          posterUrl: entry.posterUrl ?? details?.posterUrl ?? undefined,
          backdropUrl: entry.backdropUrl ?? (xtreamBackdropUrl ?? undefined) ?? details?.backdropUrl ?? undefined,
          streamId: activeVodStreamId,
          isKids: entry.isKids ?? false,
          updatedAt: Date.now(),
        });
      }
      if (now - lastProgressSaveRef.current < 5000) return; // throttle: save every 5 s
      lastProgressSaveRef.current = now;
      if (video.duration > 10) {
        setProgress(activeVodStreamId, video.currentTime, video.duration, 'movie');
      }
    };
    const onEnded = () => {
      if (activeVodStreamId && video.duration > 10) {
        setProgress(activeVodStreamId, video.duration, video.duration, 'movie');
        flushProgress();
      }
    };
    const onLicenseVerificationBlocked = () => {
      if (activeVodStreamId && video.duration > 10) {
        setProgress(activeVodStreamId, video.currentTime, video.duration, 'movie');
        flushProgress();
      }
    };
    video.addEventListener('playing',    onPlaying);
    video.addEventListener('waiting',    onWaiting);
    video.addEventListener('canplay',    onCanPlay);
    video.addEventListener('error',      onError);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended',      onEnded);
    window.addEventListener('auroraflux:license-verification-blocked', onLicenseVerificationBlocked);

    return () => {
      video.removeEventListener('playing',    onPlaying);
      video.removeEventListener('waiting',    onWaiting);
      video.removeEventListener('canplay',    onCanPlay);
      video.removeEventListener('error',      onError);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended',      onEnded);
      window.removeEventListener('auroraflux:license-verification-blocked', onLicenseVerificationBlocked);
      hlsRef.current?.destroy();
      hlsRef.current = null;
      video.src = '';
    };
  }, [streamUrl, primaryExt, activeVodStreamId, setProgress, flushProgress, addToHistory, resolvedTmdbId, entry.tmdbId, entry.title, entry.posterUrl, entry.backdropUrl, entry.isKids, t]);

  // ── Fullscreen helper — seeks + enters native fullscreen ─────────────────
  const openFullscreen = useCallback((seekPos: number) => {
    setShowResumeModal(false);
    const video = videoRef.current;
    const useAvPlay = Boolean(streamUrl && shouldUseSamsungAvPlayVod(primaryExt, streamUrl));
    if (useAvPlay && streamUrl) {
      setIsAvPlayActive(true);
      lastProgressSaveRef.current = 0;
      avPlayRef.current.start(streamUrl, seekPos, {
        onStatus: setVideoStatus,
        onTracks: (audio, subtitles) => {
          setAudioTracks(audio);
          setActiveAudioId(audio[0]?.id ?? 0);
          setSubtitleTracks(subtitles);
          setActiveSubtitleId(-1);
        },
        onTime: (currentTime, duration) => {
          if (!activeVodStreamId || duration <= 10) return;
          const now = Date.now();
          if (lastProgressSaveRef.current === 0) {
            addToHistory({
              id: String(resolvedTmdbId || entry.tmdbId || activeVodStreamId),
              type: 'movie',
              name: entry.title ?? details?.title ?? '',
              posterUrl: entry.posterUrl ?? details?.posterUrl ?? undefined,
              backdropUrl: entry.backdropUrl ?? (xtreamBackdropUrl ?? undefined) ?? details?.backdropUrl ?? undefined,
              streamId: activeVodStreamId,
              isKids: entry.isKids ?? false,
              updatedAt: now,
            });
          }
          if (now - lastProgressSaveRef.current < 5000) return;
          lastProgressSaveRef.current = now;
          setProgress(activeVodStreamId, currentTime, duration, 'movie');
        },
        onEnded: () => {
          if (activeVodStreamId) {
            const duration = avPlayRef.current.duration();
            if (duration > 10) setProgress(activeVodStreamId, duration, duration, 'movie');
            flushProgress();
          }
        },
      });
    } else {
      setIsAvPlayActive(false);
    }
    if (video) {
      if (!useAvPlay && seekPos > 0) {
        if (video.readyState >= 3) {
          video.currentTime = seekPos;
        } else {
          const seekOnReady = () => {
            video.currentTime = seekPos;
            video.removeEventListener('canplay', seekOnReady);
          };
          video.addEventListener('canplay', seekOnReady);
        }
      } else if (!useAvPlay) {
        // reset any time accumulated during silent background buffering
        video.currentTime = 0;
      }
      if (!useAvPlay) safePlay(video);
      // Request fullscreen on documentElement — avoids Chrome 47 bug where
      // fullscreen on a child of a CSS-transformed ancestor renders tiny.
      // The player wrapper uses position:fixed when isFullscreen=true, so
      // the overlay stays visible alongside the video.
      const el: any = document.documentElement;
      const p: Promise<void> | undefined =
        el.requestFullscreen?.()
        ?? el.webkitRequestFullscreen?.()
        ?? el.mozRequestFullScreen?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
      video.muted = useAvPlay;
    }
    if (activeVodStreamId) onFullScreen?.(activeVodStreamId);
  }, [activeVodStreamId, addToHistory, details, entry.backdropUrl, entry.isKids, entry.posterUrl, entry.title, entry.tmdbId, flushProgress, onFullScreen, primaryExt, resolvedTmdbId, setProgress, streamUrl, xtreamBackdropUrl]);

  // ── Fullscreen state sync ─────────────────────────────────────────────────
  useEffect(() => {
    const onFsChange = () => {
      const active = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement
      );
      isFullscreenRef.current = active;
      setIsFullscreen(active);
      if (!active && videoRef.current) {
        avPlayRef.current.close();
        setIsAvPlayActive(false);
        // Flush progress to backend if user was watching (entry exists in local storage)
        if (activeVodStreamId && getProgress(activeVodStreamId, 'movie')) {
          flushProgress();
        }
        videoRef.current.muted = true;
        setAudioTracks([]);
        setActiveAudioId(0);
        setSubtitleTracks([]);
        setActiveSubtitleId(-1);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
    };
  }, [activeVodStreamId, getProgress, flushProgress]);

  // ── Full-screen handler — checks for resumable progress first ────────────
  const handleFullScreen = useCallback(() => {
    if (!activeVodStreamId) return;
    const prog = getProgress(activeVodStreamId, 'movie');
    const pct = prog ? (prog.position / prog.duration) * 100 : 0;
    if (pct > 3 && pct < 95) {
      setPendingSeekPos(prog!.position);
      setResumeModalFocusedBtn(1);
      setShowResumeModal(true);
    } else {
      openFullscreen(0);
    }
  }, [activeVodStreamId, getProgress, openFullscreen]);

  // ── Keyboard navigation ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;

    const hasTrailer = Boolean(details?.trailerKey);
    const trailerBtnIdx = hasTrailer ? 1 : -1;
    const favBtnIdx = hasTrailer ? 2 : 1;
    const altsBtnIdx = entry.vodStreamId ? (hasTrailer ? 3 : 2) : -1;
    const BTN_COUNT = (hasTrailer ? 1 : 0) + (entry.vodStreamId ? 1 : 0) + 2; // play + [trailer] + fav + [alts]
    const recCount = similar.length || 8;

    const onKeyDown = (e: KeyboardEvent) => {
      // When in fullscreen the VideoControlsOverlay handles all keys
      if (isFullscreen) return;

      // AlternativesModal handles its own keys
      if (showAlternativesModal) return;

      // Dismiss resume modal first
      if (showResumeModal) {
        e.preventDefault();
        if (e.key === 'Escape' || e.key === 'GoBack' || e.key === 'BrowserBack') {
          setShowResumeModal(false);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          setResumeModalFocusedBtn(0);
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          setResumeModalFocusedBtn(1);
        } else if (e.key === 'Enter' || e.key === ' ') {
          if (resumeModalFocusedBtn === 0) {
            clearProgress(activeVodStreamId!, 'movie');
            openFullscreen(0);
          } else {
            openFullscreen(pendingSeekPos);
          }
        }
        return;
      }

      switch (e.key) {
        case 'Escape':
        case 'GoBack':
        case 'BrowserBack':
          e.preventDefault();
          onBack?.();
          break;

        // ── In buttons row ──────────────────────────────────
        case 'ArrowRight':
          if (focusSection === 'buttons') {
            e.preventDefault();
            setFocusedBtnIdx((prev) => Math.min(prev + 1, BTN_COUNT - 1));
          } else {
            e.preventDefault();
            setFocusedRec((prev) => Math.min(prev + 1, recCount - 1));
          }
          break;

        case 'ArrowLeft':
          if (focusSection === 'buttons') {
            e.preventDefault();
            setFocusedBtnIdx((prev) => Math.max(prev - 1, 0));
          } else {
            e.preventDefault();
            setFocusedRec((prev) => Math.max(prev - 1, 0));
          }
          break;

        case 'ArrowDown':
          if (focusSection === 'buttons' && contentDataSource !== 'xtream') {
            e.preventDefault();
            setFocusSection('recs');
            setFocusedRec(0);
          }
          break;

        case 'ArrowUp':
          if (focusSection === 'recs') {
            e.preventDefault();
            setFocusSection('buttons');
            setFocusedRec(-1);
          }
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusSection === 'buttons') {
            if (focusedBtnIdx === 0 && entry.vodStreamId) {
              handleFullScreen();
            } else if (focusedBtnIdx === trailerBtnIdx) {
              setShowTrailerModal(true);
            } else if (focusedBtnIdx === favBtnIdx) {
              onToggleFavorite?.(String(resolvedTmdbId || entry.tmdbId || entry.vodStreamId || ''));
            } else if (focusedBtnIdx === altsBtnIdx) {
              setShowAlternativesModal(true);
            }
          } else if (focusedRec >= 0 && similar[focusedRec]) {
            onSelectRecommended?.({
              tmdbId: similar[focusedRec].tmdb.id,
              title: similar[focusedRec].tmdb.title,
              posterUrl: similar[focusedRec].tmdb.posterUrl ?? undefined,
              backdropUrl: similar[focusedRec].tmdb.backdropUrl ?? undefined,
              vodStreamId: similar[focusedRec].vodStreamId,
              plot: similar[focusedRec].tmdb.overview,
              releaseDate: similar[focusedRec].tmdb.releaseDate,
            });
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    isActive, isFullscreen, focusSection, focusedBtnIdx, focusedRec, details,
    similar, entry, onBack, onSelectRecommended, handleFullScreen, onToggleFavorite,
    showResumeModal, resumeModalFocusedBtn, activeVodStreamId, pendingSeekPos,
    clearProgress, openFullscreen, contentDataSource, showAlternativesModal, resolvedTmdbId,
  ]);

  // ── Subtitle: show Xtream stream name when it meaningfully differs from TMDB title ───
  function normalizeTitle(s: string): string {
    return s
      .replace(/\(\d{4}\)/g, '')   // (2025)
      .replace(/\[.*?\]/g, '')     // [LEG], [DUB], [4K]
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }
  const xtreamSubtitle: string | null = (() => {
    if (!xtreamVodName || !details?.title) return null;
    return normalizeTitle(xtreamVodName) !== normalizeTitle(details.title)
      ? xtreamVodName
      : null;
  })();

  // ── Derived display values ───────────────────────────────────────────────
  const title = details?.title ?? entry.title ?? '…';
  const rating = details ? details.voteAverage.toFixed(1) : '—';
  const countryDateOriginal = details
    ? [
        details.countries[0] ?? '',
        formatDate(details.releaseDate),
        details.originalTitle !== details.title ? details.originalTitle : '',
      ]
        .filter(Boolean)
        .join(' | ')
    : '';
  const genres = details?.genres.join(', ') ?? '';
  const director = details?.director ?? '';
  const cast = details?.cast.join(', ') ?? '';
  const overview = details?.overview ?? '';

  const backdropStyle: React.CSSProperties = (xtreamBackdropUrl || details?.backdropUrl)
    ? { backgroundImage: `url(${xtreamBackdropUrl || details?.backdropUrl})` }
    : {};

  const loadingPlaceholders = Array.from({ length: 8 });

  return (
    <div className="movie-details">
      {/* Blurred ambient backdrop */}
      <div className="movie-details__backdrop" style={backdropStyle} />

      {/* ── Mini player ────────────────────────────────────── */}
      <div
        ref={fsWrapRef}
        className={`movie-details__player${isFullscreen ? ' movie-details__player--fullscreen' : ''}`}
      >
        {/* Video element: always in DOM when streamUrl exists (HLS preload + fullscreen) */}
        {streamUrl && (
          <>
            <video
              ref={videoRef}
              className={`movie-details__video${(!isFullscreen && movieAutoplayMode !== 'movie') ? ' movie-details__video--hidden' : ''}`}
              style={isAvPlayActive ? { display: 'none' } : undefined}
              muted
              playsInline
              onError={() => setVideoStatus('error')}
            />
            <object
              type="application/avplayer"
              aria-label="Samsung AVPlay"
              style={{ display: isAvPlayActive ? 'block' : 'none', position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            />
            {isFullscreen && videoStatus && (
              <PlaybackStatusOverlay status={videoStatus} />
            )}
            {isFullscreen && (
              <VideoControlsOverlay
                videoRef={videoRef}
                title={details?.title ?? entry.title ?? ''}
                externalPlayback={isAvPlayActive ? avPlayRef.current : undefined}
              />
            )}
          </>
        )}
        {/* Mini player foreground: trailer iframe or placeholder */}
        {!isFullscreen && !showTrailerModal && movieAutoplayMode !== 'movie' && (
          movieAutoplayMode === 'trailer' && details?.trailerKey ? (
            <iframe
              src={`https://ott-player-trailer.enrico-mendonca.workers.dev/?v=${details.trailerKey}&mode=preview`}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              scrolling="no"
              allow="autoplay; fullscreen; encrypted-media"
              title={t('trailerModal.player')}
            />
          ) : movieAutoplayMode === 'trailer' ? (
            <div
              className="movie-details__player-placeholder"
              style={{
                backgroundImage: `url(${details?.posterUrl || xtreamBackdropUrl || details?.backdropUrl || ''})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          ) : (
            <div className="movie-details__player-placeholder">
              <div className="movie-details__player-placeholder-icon">
                <FiPlay size={28} />
              </div>
            </div>
          )
        )}
      </div>

      {/* ── Info column ────────────────────────────────────── */}
      <div className="movie-details__info">
        {loadingDetails ? (
          <div className="movie-details__info-skeleton">
            <div className="movie-details__title-row">
              <span className="movie-details__title">{entry.title || '…'}</span>
            </div>
            <div className="movie-details__skel movie-details__skel--meta" />
            <div className="movie-details__skel movie-details__skel--meta" />
            <div className="movie-details__skel movie-details__skel--meta" />
            <div className="movie-details__skel movie-details__skel--paragraph" />
            <div className="movie-details__skel movie-details__skel--paragraph movie-details__skel--paragraph-short" />
          </div>
        ) : (
          <>
            {/* Title + rating */}
            <div className="movie-details__title-row">
              <span className="movie-details__title">{title}</span>
              <span className="movie-details__rating">{rating}</span>
            </div>
            {xtreamSubtitle && (
              <p className="movie-details__xtream-subtitle">{t('movieDetails.fromServer')}{xtreamSubtitle}</p>
            )}

            {/* Country | Date | Original title */}
            {countryDateOriginal && (
              <p className="movie-details__meta">{countryDateOriginal}</p>
            )}

            {/* Genre */}
            {genres && (
              <p className="movie-details__meta">
                <strong>{t('movieDetails.genre')}</strong>{genres}
              </p>
            )}

            {/* Director */}
            {director && (
              <p className="movie-details__meta">
                <strong>{t('movieDetails.director')}</strong>{director}
              </p>
            )}

            {/* Cast */}
            {cast && (
              <p className="movie-details__meta">
                <strong>{t('movieDetails.cast')}</strong>{cast}
              </p>
            )}

            {/* Synopsis */}
            {overview && (
              <p className="movie-details__synopsis">
                <strong>{t('movieDetails.synopsis')}</strong>
                {overview}
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Action buttons ──────────────────────────────────── */}
      <div className="movie-details__actions">
        <button
          className={`movie-details__action-btn${focusSection === 'buttons' && focusedBtnIdx === 0 ? ' movie-details__action-btn--focused' : ''}`}
          onClick={handleFullScreen}
          disabled={!activeVodStreamId}
          title={activeVodStreamId ? t('movieDetails.fullscreenAria') : t('movieDetails.notAvailable')}
        >
          <FiPlay size={16} />
          {t('movieDetails.watch')}
        </button>

        {details?.trailerKey && (
          <button
            className={`movie-details__action-btn${focusSection === 'buttons' && focusedBtnIdx === (details.trailerKey ? 1 : -1) ? ' movie-details__action-btn--focused' : ''}`}
            onClick={() => setShowTrailerModal(true)}
          >
            <FiPlay size={16} />
            {t('movieDetails.watchTrailer')}
          </button>
        )}

        <button
          className={`movie-details__action-btn${isFavorite ? ' movie-details__action-btn--fav-active' : ''}${focusSection === 'buttons' && focusedBtnIdx === (details?.trailerKey ? 2 : 1) ? ' movie-details__action-btn--focused' : ''}`}
          onClick={() => onToggleFavorite?.(String(resolvedTmdbId || entry.tmdbId || entry.vodStreamId || ''))}
          disabled={!resolvedTmdbId && !entry.tmdbId && !entry.vodStreamId}
        >
          <FiHeart size={16} />
          {t('movieDetails.favorite')}
        </button>

        {entry.vodStreamId && (() => {
          const hasTrailer = Boolean(details?.trailerKey);
          const altsBtnIdx = hasTrailer ? 3 : 2;
          return (
            <button
              className={`movie-details__action-btn${focusSection === 'buttons' && focusedBtnIdx === altsBtnIdx ? ' movie-details__action-btn--focused' : ''}`}
              onClick={() => setShowAlternativesModal(true)}
            >
              <FiFilm size={16} />
              {t('alternatives.button')}
            </button>
          );
        })()}
      </div>


      {/* ── Recommended ─────────────────────────────────────── */}
      {contentDataSource !== 'xtream' && !entry.isKids && (loadingSimilar || similar.length > 0) && (
      <div className="movie-details__recommended">
        <p className="movie-details__recommended-title">{t('movieDetails.recommended')}</p>
        <div className="movie-details__recommended-row">
          {loadingSimilar
            ? loadingPlaceholders.map((_, i) => (
                <RecCard
                  key={i}
                  loading
                  focused={false}
                  onSelect={() => {}}
                />
              ))
            : similar.map((rec, i) => (
                <RecCard
                  key={rec.tmdb.id}
                  movie={rec.tmdb}
                  focused={focusedRec === i}
                  onSelect={() =>
                    onSelectRecommended?.({
                      tmdbId: rec.tmdb.id,
                      title: rec.tmdb.title,
                      posterUrl: rec.tmdb.posterUrl ?? undefined,
                      backdropUrl: rec.tmdb.backdropUrl ?? undefined,
                      vodStreamId: rec.vodStreamId,
                      plot: rec.tmdb.overview,
                      releaseDate: rec.tmdb.releaseDate,
                    })
                  }
                />
              ))}
        </div>
      </div>
      )}

      {/* ── Resume modal ────────────────────────────────────── */}
      {showResumeModal && (
        <div className="movie-details__resume-overlay">
          <div className="movie-details__resume-modal">
            <p className="movie-details__resume-title">{t('movieDetails.resumeTitle')}</p>
            <div className="movie-details__resume-btns">
              <button
                className={`movie-details__resume-btn${resumeModalFocusedBtn === 0 ? ' movie-details__resume-btn--focused' : ''}`}
                onClick={() => { clearProgress(activeVodStreamId!, 'movie'); openFullscreen(0); }}
              >
                <FiPlay size={14} />
                {t('movieDetails.playFromStart')}
              </button>
              <button
                className={`movie-details__resume-btn movie-details__resume-btn--primary${resumeModalFocusedBtn === 1 ? ' movie-details__resume-btn--focused' : ''}`}
                onClick={() => openFullscreen(pendingSeekPos)}
              >
                <FiPlay size={14} />
                {t('movieDetails.continueFrom')}{formatProgressTime(pendingSeekPos)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Trailer modal ────────────────────────────────────── */}
      {showTrailerModal && details?.trailerKey && (
        <TrailerModal
          trailerKey={details.trailerKey}
          onClose={() => setShowTrailerModal(false)}
        />
      )}

      {/* ── Alternatives modal ───────────────────────────────── */}
      {showAlternativesModal && entry.vodStreamId && (
        <AlternativesModal
          currentName={entry.title ?? details?.title ?? ''}
          currentStreamId={entry.vodStreamId}
          type="movie"
          onSelect={(item: XtreamContent) => {
            setShowAlternativesModal(false);
            if (String(item.stream_id ?? '') === String(entry.vodStreamId)) return;
            onSelectRecommended?.({
              tmdbId: 0,
              title: item.name,
              posterUrl: item.stream_icon || item.cover || undefined,
              vodStreamId: String(item.stream_id ?? ''),
              plot: item.plot || undefined,
              year: item.year || undefined,
              releaseDate: item.releaseDate || item.release_date || undefined,
            });
          }}
          onClose={() => setShowAlternativesModal(false)}
        />
      )}

    </div>
  );
};
